import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { App, Host, JSONSafe } from "@/types";
import { Template } from "@/types/template";
import { useState } from "react";
import useSWR from "swr";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectHost } from "@/components/SelectHost";
import { SelectStringValue } from "@/components/SelectValue";
import { Stepper } from "@/components/Stepper";
import { LaunchHeader } from "@/components/launch/LaunchHeader";
import { useRouter } from "next/router";
import { fetcherTyped, str } from "@/lib/utils";
import { SSHKeyListResponseSchema } from "@/types/schemas";

export const ExistingHost = ({
  template,
  onNext
}: {
  template: Template;
  onNext?: (
    choice: "host" | "provision" | "existing" | "launch",
    host?: {
      hostId?: string;
      hostname?: string;
      username?: string;
      sshKeyId?: string;
    }
  ) => void;
}) => {
  const router = useRouter();
  const [username, setUsername] = useState(str(router.query.username));
  const [hostname, setHostname] = useState(str(router.query.hostname));
  const { data: sshKeys } = useSWR(
    "/api/settings/privateKey",
    fetcherTyped(SSHKeyListResponseSchema)
  );
  const [sshKeyId, setSshKeyId] = useState<string | undefined>(str(router.query.sshKeyId));
  const { data: hosts } = useSWR<JSONSafe<Host[]>>("/api/host");
  const [hostId, setHostId] = useState<string | undefined>(str(router.query.hostId));
  const { data: hostApps } = useSWR<JSONSafe<App>[]>(
    hostId ? `/api/app?hostId=${hostId}` : null
  );
  const [tab, setTab] = useState(
    router.query.sshKeyId ? "new" : (str(router.query.tab) ?? "existing")
  );

  return (
    <div className="flex flex-col justify-center items-center w-full min-h-screen p-6">
      <LaunchHeader
        name={template?.name}
        logo={template?.logo}
        subtitle="Install app on an existing host"
        className="mb-2"
      />

      <div className="mt-6 flex flex-col md:flex-row gap-6 mb-8">
        <Card className="w-[300px] md:w-[500px]">
          <CardHeader>
            <CardTitle>Bring Your Own Server</CardTitle>
            <CardDescription>Use an existing server you own</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Tabs
              value={tab}
              onValueChange={v => {
                setTab(v);
                if (v === "new") {
                  setHostname("");
                  setUsername("");
                  setSshKeyId(undefined);
                } else {
                  setHostId(undefined);
                  setHostname("");
                  setUsername("");
                }
              }}
            >
              <TabsList>
                <TabsTrigger value="existing">Existing</TabsTrigger>
                <TabsTrigger value="new">New</TabsTrigger>
              </TabsList>

              <TabsContent value="new">
                <div className="text-secondary-foreground text-sm mb-4">
                  Add a new existing host
                </div>

                <div className="grid gap-6">
                  <div className="flex flex-col">
                    <Label htmlFor="hostname">Hostname</Label>
                    <Input
                      type="text"
                      className="w-full mt-3 mb-1"
                      value={hostname}
                      placeholder={`${template.id}.example.com`}
                      onChange={e => setHostname(e.target.value)}
                    />

                    <div className="text-secondary-foreground text-sm">
                      Make sure to create a DNS record which point to your host. It will be
                      used to run this app.
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      type="text"
                      className="w-full mt-3 mb-1"
                      value={username}
                      placeholder="Username"
                      onChange={e => setUsername(e.target.value)}
                    />

                    <div className="text-secondary-foreground text-sm">
                      Local username to connect to the host via SSH with.
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <Label htmlFor="sshKeyId">SSH key</Label>
                    <Select value={sshKeyId} onValueChange={setSshKeyId}>
                      <SelectTrigger id="sshKeyId">
                        <SelectValue placeholder="Select SSH key" />
                      </SelectTrigger>
                      <SelectContent>
                        {sshKeys?.map(k => (
                          <SelectItem key={k.id} value={k.id}>
                            Existing SSH key: {k.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="existing">
                <div className="text-secondary-foreground text-sm mb-4">
                  Use already added host
                </div>

                <div className="grid gap-6">
                  <div className="grid gap-3">
                    <Label htmlFor="hostId">Host</Label>
                    <SelectHost
                      id="hostId"
                      hosts={hosts ?? []}
                      host={hosts?.find(h => h.id === hostId)}
                      onSelect={host => setHostId(host?.id)}
                    />
                  </div>

                  <div className="grid gap-3">
                    <Label htmlFor="hostname">Hostname</Label>
                    <SelectStringValue
                      id="hostname"
                      value={hostname}
                      disabled={!hostId}
                      disabledValue={hostname =>
                        !!hostApps?.find(app => app.hostname === hostname)
                      }
                      values={hosts?.find(h => h.id === hostId)?.hostnames ?? []}
                      onSelect={value => setHostname(value ?? "")}
                      labelRenderer={hostname =>
                        hostname +
                        " " +
                        (hostApps?.find(app => app.hostname === hostname)
                          ? " (" + hostApps?.find(app => app.hostname === hostname)?.name + ")"
                          : "")
                      }
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <Button
        size="lg"
        disabled={
          tab === "existing" ? !hostId || !hostname : !hostname || !username || !sshKeyId
        }
        onClick={() => {
          if (onNext) {
            if (tab == "existing" && hostId && hostname) {
              onNext("launch", { hostId, hostname });
            } else if (tab === "new" && hostname && username && sshKeyId) {
              onNext("launch", {
                hostname,
                username,
                sshKeyId
              });
            }
          }
        }}
      >
        Next
      </Button>

      <div className="mt-8">
        <Stepper
          steps={3}
          current={1}
          onStep={step => {
            if (step === 0) {
              onNext && onNext("host");
            }
          }}
        />
      </div>
    </div>
  );
};
